import axios, {
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import type { AuthResponse } from '../types';
import { store } from '../redux/store';
import { authSuccess, logout } from '../redux/authSlice';

const rawBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
const API_BASE_URL = rawBaseUrl.replace(/\/+$/, '');

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
});

const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
});

const canUseBrowserStorage = () => typeof window !== 'undefined' && !!window.localStorage;

const readToken = (key: string): string | null => {
  if (!canUseBrowserStorage()) {
    return null;
  }
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    console.warn(`读取本地存储字段 ${key} 失败`, error);
    return null;
  }
};

const navigateToLogin = () => {
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
};

let refreshPromise: Promise<AuthResponse> | null = null;

const requestAccessTokenRefresh = (): Promise<AuthResponse> | null => {
  const refreshToken = readToken('refresh_token');
  if (!refreshToken) {
    return null;
  }
  if (!refreshPromise) {
    refreshPromise = refreshClient
      .post<AuthResponse>('/auth/refresh', {
        refresh_token: refreshToken,
      })
      .then((response) => {
        const data = response.data;
        store.dispatch(authSuccess(data));
        return data;
      })
      .catch((error) => {
        store.dispatch(logout());
        navigateToLogin();
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

apiClient.interceptors.request.use((config) => {
  if (canUseBrowserStorage()) {
    const token = readToken('access_token');
    if (token) {
      config.headers = config.headers || {};
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const { response } = error;
    const originalRequest = error.config as RetryableRequestConfig | undefined;

    if (!response || !originalRequest) {
      return Promise.reject(error);
    }

    if (response.status !== 401) {
      return Promise.reject(error);
    }

    const requestUrl = originalRequest.url ?? '';
    const isAuthEndpoint =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/register') ||
      requestUrl.includes('/auth/refresh');

    if (originalRequest._retry || isAuthEndpoint) {
      return Promise.reject(error);
    }

    const refreshResult = requestAccessTokenRefresh();
    if (!refreshResult) {
      store.dispatch(logout());
      navigateToLogin();
      return Promise.reject(error);
    }

    try {
      const data = await refreshResult;
      originalRequest._retry = true;
      originalRequest.headers = originalRequest.headers || {};
      (originalRequest.headers as Record<string, string>).Authorization = `Bearer ${data.access_token}`;
      return apiClient(originalRequest as AxiosRequestConfig);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }
);

export default apiClient;
export { API_BASE_URL };
